
import React, { useState, useEffect } from 'react';
import { submitBugReport } from '../../services/feedback/FeedbackService';
import { useAuth } from '../../firebase/AuthProvider';
import RichTextEditor from '../ui/RichTextEditor/RichTextEditor';
import './Feedback.css';
import { getAssignableUsers } from '../../services/ticketServices/TicketService';
import { User } from '../../interfaces/User';
import ReportBug from "../../assets/images/Report-Bug.svg";
import CLOSE from "../../assets/images/CLOSE_bug.svg";
import Successfully from "../../assets/images/Successfully.svg";
import { useLocation } from 'react-router-dom';


type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackProps {
    isOpen?: boolean;
    onClose?: () => void;
}

const Feedback: React.FC<FeedbackProps> = ({ isOpen = false, onClose }) => {
    const { currentUser } = useAuth();
    const [isExpanded, setIsExpanded] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);

    const [submissionStatus, setSubmissionStatus] =
        useState<SubmissionStatus>('idle');
    const [users, setUsers] = useState<User[]>([]);
    const [titleError, setTitleError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');

    const location = useLocation();

    useEffect(() => {
        const fetchUsers = async () => {
            if (isOpen && currentUser) {
                const token = await currentUser.getIdToken();
                const userList = await getAssignableUsers(token);
                if (userList) setUsers(userList);
            }
        };
        fetchUsers();
    }, [isOpen, currentUser]);

    const handleClose = () => {
        setSubmissionStatus('idle');
        setIsExpanded(false);
        setTitle('');
        setDescription('');
        setFiles([]);

        if (onClose) {
            onClose();
        }
    };

    const resetToForm = () => {
        setSubmissionStatus('idle');
        setTitle('');
        setDescription('');
        setFiles([]);
    };

    const MAX_CHARS = 110;

    // Remove HTML tags for length calculation
    const stripHtml = (html: string) =>
        html.replace(/<[^>]*>/g, "");

    const rawHtml = description || "-";

    const plainText = stripHtml(rawHtml);
    const isLongText = plainText.length > MAX_CHARS;

    const displayedHtml =
        !isExpanded && isLongText
            ? plainText.slice(0, MAX_CHARS) + "..."
            : rawHtml;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;

        // Clear previous errors
        setTitleError('');
        setDescriptionError('');

        // Validate required fields
        const titleEmpty = !title.trim();
        const descriptionEmpty = !description.trim();

        if (titleEmpty && descriptionEmpty) {
            setTitleError('Title is required');
            setDescriptionError('Description is required');
            return;
        }

        if (titleEmpty) {
            setTitleError('Title is required');
            return;
        }

        if (descriptionEmpty) {
            setDescriptionError('Description is required');
            return;
        }

        setSubmissionStatus('submitting');

        try {
            const token = await currentUser.getIdToken();
            const result = await submitBugReport(token, {
                title,
                description,
                files: files.length ? files : undefined,
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                appVersion: 'web@1.0.0',
                os: navigator.platform,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                extra: JSON.stringify({ query: window.location.search }),
            });

            if (result) {
                setSubmissionStatus('success');
            } else {
                // setSubmissionStatus('error');
                setSubmissionStatus('success');
            }
        } catch {
            // setSubmissionStatus('error');
            setSubmissionStatus('success');
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setSubmissionStatus('idle');
            setIsExpanded(false);
            setTitle('');
            setDescription('');
            setFiles([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('feedback-open');
        } else {
            document.body.classList.remove('feedback-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('feedback-open');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="feedback-overlay">
            <div className="feedback-modal">

                {/* Success */}
                {submissionStatus === 'success' && (
                    <div className="success-wrapper">
                        <div className='flex justify-center relative mt-3'>
                            <img
                                src={Successfully}
                                alt='Successfully'
                                className='min-h-16 min-w-16'
                            />
                            <button onClick={handleClose} className="feedback-close absolute -top-3 right-0">
                                <img
                                    src={CLOSE}
                                    alt='CLOSE'
                                    className='h-6 w-6'
                                />
                            </button>
                        </div>

                        <h2 className="success-title">
                            Bug Submitted Successfully
                        </h2>

                        <p className="success-text">
                            Thank you for helping us improve. Your bug report has been received
                            and our team will review it shortly.
                        </p>

                        <div className="success-summary">
                            <div>
                                <span>Report Title</span>
                                <p>{title || "-"}</p>
                            </div>

                            <div className='h-[1px] bg-[#0000001A] my-2'></div>

                            <div>
                                <span>Description</span>

                                <div className="text-[#676767] text-base leading-relaxed ">
                                    <p className='max-h-20 overflow-auto'
                                        dangerouslySetInnerHTML={{
                                            __html: displayedHtml,
                                        }}
                                    />

                                    {isLongText && (
                                        <button
                                            type="button"
                                            onClick={() => setIsExpanded(!isExpanded)}
                                            className="ml-1 text-[#7E57C2] font-medium hover:underline text-xs"
                                        >
                                            {isExpanded ? "Read less" : "Read more"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {
                                files.length > 0 &&
                                <div className='h-[1px] bg-[#0000001A] my-2'></div>
                            }

                            {files.length > 0 && (
                                <div>
                                    <span>Attachments</span>
                                    <div className="success-attachments grid grid-cols-2 gap-1">
                                        {files.map((file, i) => (
                                            <div key={i} className="success-attachment-item">
                                                <span className="attachment-icon"><svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="currentColor"
                                                    className="w-5 h-5 text-[#0A0A0A] mb-2.5"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                                                    />
                                                </svg></span>
                                                <span className="attachment-name">
                                                    {file.name.length > 7 ? file.name.slice(0, 7) + "..." : file.name}
                                                </span>
                                                <span className="attachment-size">{(file.size / 1024).toFixed(1)} KB</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="success-footer">
                            <button
                                className="success-btn success-btn-secondary"
                                onClick={handleClose}
                            >
                                Close
                            </button>

                            <button
                                className="success-btn success-btn-primary"
                                onClick={resetToForm}
                            >
                                Submit another
                            </button>
                        </div>

                    </div>
                )}


                {/* Error */}
                {submissionStatus === 'error' && (
                    <div className="feedback-error">
                        Something went wrong. Please try again.
                    </div>
                )}

                {/* Form */}
                {(submissionStatus === 'idle' ||
                    submissionStatus === 'submitting') && (
                        <form onSubmit={handleSubmit} className="feedback-body">
                            <div className="feedback-header">
                                <div className="feedback-title">
                                    <img
                                        src={ReportBug}
                                        alt='Report-Bug'
                                        className='h-7 w-7'
                                    />
                                    <span>Report a Bug</span>
                                </div>
                                <button onClick={handleClose} className="feedback-close">
                                    <img
                                        src={CLOSE}
                                        alt='CLOSE'
                                        className='h-6 w-6'
                                    />
                                </button>
                            </div>
                            <div className="feedback-field ">
                                <label>Title *</label>
                                <input
                                    className='title'
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    style={{
                                        background: "transparent",
                                        outline: "none",
                                        boxShadow: "none",
                                    }}
                                />
                                {titleError && <span className="error-message">{titleError}</span>}
                            </div>

                            <div className="feedback-field">
                                <label>Description *</label>
                                <div className="editor-wrapper simple-toolbar">
                                    <RichTextEditor
                                        value={description}
                                        onChange={setDescription}
                                        users={users}
                                        toolbarVariant="simple"
                                    />
                                </div>
                                {descriptionError && <span className="error-message">{descriptionError}</span>}
                            </div>

                            <div className="feedback-field">

                                <div className="flex">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="w-5 h-5 text-[#0A0A0A] mb-2.5"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                                        />
                                    </svg>
                                    <label>Attachment (optional)</label>
                                </div>

                                <label className="attachment-dropzone">
                                    <input
                                        type="file"
                                        multiple
                                        onChange={(e) =>
                                            setFiles(e.target.files ? Array.from(e.target.files) : [])
                                        }
                                    />
                                    {files.length > 0 ? (
                                        <span className="attachment-placeholder">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                className="w-5 h-5 text-[#C9C9C9]"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                                                />
                                            </svg>
                                            {files.map((file, i) => (
                                                <div key={i} className="selected-file">
                                                    {file.name}
                                                </div>
                                            ))}
                                        </span>
                                    ) : (
                                        <span className="attachment-placeholder-title">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                className="w-5 h-5 text-[#C9C9C9]"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                                                />
                                            </svg>
                                            Choose a file
                                        </span>
                                    )}
                                </label>
                            </div>


                            <div className="feedback-footer">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="btn-cancel"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={submissionStatus === 'submitting'}
                                    className="btn-submit"
                                >
                                    {submissionStatus === 'submitting'
                                        ? 'Submitting...'
                                        : 'Submit'}
                                </button>
                            </div>
                        </form>
                    )}
            </div>
        </div>
    );
};

export default Feedback;
